/**
 * Property Modifiers Handler
 * Manages price-modifying line item properties
 */

class PropertyModifiers {
  constructor(container) {
    this.container = container;
    this.dataScript = container.querySelector('[data-modifiers-data]');
    
    if (!this.dataScript) {
      console.warn('Property modifiers data not found');
      return;
    }

    try {
      this.data = JSON.parse(this.dataScript.textContent);
    } catch (e) {
      console.error('Failed to parse property modifiers data', e);
      return;
    }

    this.modifiers = this.data.modifiers || [];
    this.basePrice = this.data.basePrice || 0;
    this.currencyFormat = this.data.currencyFormat || '${{amount}}';
    
    this.currentModifications = {};
    this.productForm = document.querySelector('product-form');
    this.priceElement = document.querySelector(`#price-${this.getSectionId()}`);
    this.mobilePriceElement = document.querySelector(`#mobile-price-${this.getSectionId()}`);
    
    this.init();

    this.container._propertyModifiersInstance = this;
  }

  init() {
    this.attachEventListeners();
    this.updateTotalPrice();
  }

  getSectionId() {
    const productInfo = this.container.closest('product-info');
    return productInfo ? productInfo.dataset.section : '';
  }

  attachEventListeners() {
    // Handle select dropdowns
    this.container.querySelectorAll('select[data-property-type="select"]').forEach(select => {
      select.addEventListener('change', (e) => this.handleSelectChange(e));
    });

    // Handle boolean checkboxes
    this.container.querySelectorAll('input[data-property-type="boolean"]').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => this.handleBooleanChange(e));
    });

    // Handle conditional text inputs AND textareas
    this.container.querySelectorAll('input[data-property-type="conditional-text"], textarea[data-property-type="conditional-text"]').forEach(input => {
      input.addEventListener('input', (e) => this.handleConditionalTextInput(e));
    });

    // Add form validation
    if (this.productForm) {
      const form = this.productForm.querySelector('form');
      if (form) {
        form.addEventListener('submit', (e) => {
          if (!this.validateProperties()) {
            e.preventDefault();
            // Don't use stopImmediatePropagation - let other validators run too
            return false;
          }
        }, true);
      }
    }
  }

  validateProperties() {
    let isValid = true;
    let firstInvalidField = null;

    this.modifiers.forEach(modifier => {
      if (!modifier.required) return;

      const propertyName = modifier.property_name;
      
      if (modifier.type === 'select') {
        const select = this.container.querySelector(`select[data-property-name="${propertyName}"]`);
        if (!select || !select.value) {
          isValid = false;
          console.log('invalid firing 2');
          this.showFieldError(propertyName, 'This field is required');
          if (!firstInvalidField) {
            firstInvalidField = select;
          }
        }
      } else if (modifier.type === 'boolean') {
        const checkbox = this.container.querySelector(`input[data-property-name="${propertyName}"]`);
        
        if (modifier.conditional_input) {
          // If checkbox is checked, text input/textarea is required
          if (checkbox && checkbox.checked) {
            const textInput = this.container.querySelector(`input[data-conditional-for="${propertyName}"]`);
            const textarea = this.container.querySelector(`textarea[data-conditional-for="${propertyName}"]`);
            const conditionalField = textInput || textarea;
            
            if (!conditionalField || !conditionalField.value.trim()) {
              isValid = false;
              this.showFieldError(propertyName, 'Please enter text for this option');
              if (!firstInvalidField) {
                firstInvalidField = conditionalField;
              }
            }
          } else if (modifier.required) {
            // Checkbox itself is required to be checked
            isValid = false;
            console.log('invalid firing 3');
            this.showFieldError(propertyName, 'This field is required');
            if (!firstInvalidField) {
              firstInvalidField = checkbox;
            }
          }
        } else {
          // Simple checkbox - must be checked if required
          if (!checkbox || !checkbox.checked) {
            isValid = false;
            console.log('invalid firing 4');
            this.showFieldError(propertyName, 'This field is required');
            if (!firstInvalidField) {
              firstInvalidField = checkbox;
            }
          }
        }
      }
    });

    // Focus first invalid field if form submission fails
    if (!isValid && firstInvalidField) {
      setTimeout(() => {
        firstInvalidField.focus();
        firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }

    return isValid;
  }

  handleSelectChange(event) {
    const select = event.target;
    const modifierIndex = parseInt(select.dataset.modifierIndex);
    const modifier = this.modifiers[modifierIndex];
    const selectedOption = select.options[select.selectedIndex];
    const priceModifier = parseFloat(selectedOption.dataset.priceModifier || 0);

    // Update current modifications
    this.currentModifications[modifier.property_name] = {
      value: select.value,
      priceModifier: priceModifier
    };

    // Update the hidden price input
    const priceInput = this.container.querySelector(`input[data-price-input-for="${modifier.property_name}"]`);
    if (priceInput) {
      priceInput.value = priceModifier;
    }

    // Clear error for this field
    this.clearFieldError(modifier.property_name);

    this.updateTotalPrice();
  }

  handleBooleanChange(event) {
    const checkbox = event.target;
    const modifierIndex = parseInt(checkbox.dataset.modifierIndex);
    const modifier = this.modifiers[modifierIndex];
    const propertyName = checkbox.dataset.propertyName;
    
    // Find the price modifier for the true value
    let priceModifier = 0;
    if (checkbox.checked) {
      const trueOption = modifier.options.find(opt => 
        opt.value.toLowerCase() === 'true' || opt.value.toLowerCase() === 'yes'
      );
      priceModifier = trueOption ? parseFloat(trueOption.price_modifier) : 0;
    }

    // Update the hidden price input
    const priceInput = this.container.querySelector(`input[data-price-input-for="${propertyName}"]`);
    if (priceInput) {
      priceInput.value = checkbox.checked ? priceModifier : 0;
    }

    // Handle conditional text input OR textarea
    const conditionalTextInput = this.container.querySelector(`input[data-conditional-for="${propertyName}"]`);
    const conditionalTextarea = this.container.querySelector(`textarea[data-conditional-for="${propertyName}"]`);
    const conditionalInput = conditionalTextInput || conditionalTextarea;
    
    const conditionalTextWrapper = this.container.querySelector(`.conditional-text-input[data-conditional-for="${propertyName}"]`);
    const conditionalTextareaWrapper = this.container.querySelector(`.conditional-textarea[data-conditional-for="${propertyName}"]`);
    const conditionalWrapper = conditionalTextWrapper || conditionalTextareaWrapper;
    
    if (conditionalInput && conditionalWrapper) {
      if (checkbox.checked) {
        // Show and enable the text input/textarea
        conditionalWrapper.style.display = 'block';
        conditionalInput.disabled = false;
        
        // The property value will come from the text input/textarea, not the checkbox
        // So we don't set currentModifications yet
        this.currentModifications[propertyName] = {
          value: '', // Will be filled by text input/textarea
          priceModifier: priceModifier,
          requiresText: true
        };
      } else {
        // Hide and disable the text input/textarea
        conditionalWrapper.style.display = 'none';
        conditionalInput.disabled = true;
        conditionalInput.value = '';
        
        // Remove from modifications
        delete this.currentModifications[propertyName];
        
        // Update character count if exists
        const charCount = this.container.querySelector(`.character-count[data-count-for="${propertyName}"]`);
        if (charCount) {
          charCount.textContent = `0 / ${modifier.character_limit} characters`;
        }
      }
    } else {
      // No conditional input, just update the modification
      if (checkbox.checked) {
        const trueOption = modifier.options.find(opt => 
          opt.value.toLowerCase() === 'true' || opt.value.toLowerCase() === 'yes'
        );
        this.currentModifications[propertyName] = {
          value: trueOption ? trueOption.value : 'Yes',
          priceModifier: priceModifier
        };
      } else {
        delete this.currentModifications[propertyName];
      }
    }

    // Clear error for this field
    this.clearFieldError(propertyName);

    this.updateTotalPrice();
  }

  handleConditionalTextInput(event) {
    const input = event.target;
    const propertyName = input.dataset.conditionalFor;
    const modifier = this.modifiers.find(m => m.property_name === propertyName);
    
    if (!modifier) return;

    // Update character count
    if (modifier.character_limit > 0) {
      const charCount = this.container.querySelector(`.character-count[data-count-for="${propertyName}"]`);
      if (charCount) {
        charCount.textContent = `${input.value.length} / ${modifier.character_limit} characters`;
      }
    }

    // Update the value in currentModifications (keep the price modifier)
    if (this.currentModifications[propertyName]) {
      this.currentModifications[propertyName].value = input.value;
    }

    // Clear error for this field
    this.clearFieldError(propertyName);
  }

  showFieldError(propertyName, message) {
    // Find the parent field wrapper using a more robust approach
    // Loop through all property-modifier-field elements to find the matching one
    let fieldWrapper = null;
    const allFields = this.container.querySelectorAll('.property-modifier-field');
    
    for (const field of allFields) {
      if (field.getAttribute('data-property-name') === propertyName) {
        fieldWrapper = field;
        break;
      }
    }
    
    if (!fieldWrapper) {
      console.warn('Field wrapper not found for:', propertyName);
      console.log('Available fields:', Array.from(allFields).map(f => f.getAttribute('data-property-name')));
      return;
    }

    const errorId = `property-error-${propertyName.replace(/[^a-zA-Z0-9]/g, '-')}`;
    let errorEl = fieldWrapper.querySelector('.contact__field-error');
    
    if (!errorEl) {
      errorEl = document.createElement('small');
      errorEl.className = 'contact__field-error';
      errorEl.id = errorId;
      errorEl.style.cssText = 'display: block; margin-top: 0.5rem;';
      errorEl.innerHTML = `
        <span class="visually-hidden">Error</span>
        <span class="form__message">
          <span class="svg-wrapper">${this.getErrorIcon()}</span>
          ${message}
        </span>
      `;
      fieldWrapper.appendChild(errorEl);
      console.log('Error message appended for:', propertyName);
    } else {
      // Update existing error message
      const messageSpan = errorEl.querySelector('.form__message');
      if (messageSpan) {
        const iconHTML = `<span class="svg-wrapper">${this.getErrorIcon()}</span>`;
        messageSpan.innerHTML = iconHTML + message;
      }
    }

    // Set aria attributes on the input
    const input = fieldWrapper.querySelector('select, input[type="checkbox"], input[type="text"], textarea');
    if (input) {
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', errorId);
    }
  }

  clearFieldError(propertyName) {
    // Find the parent field wrapper using a more robust approach
    let fieldWrapper = null;
    const allFields = this.container.querySelectorAll('.property-modifier-field');
    
    for (const field of allFields) {
      if (field.getAttribute('data-property-name') === propertyName) {
        fieldWrapper = field;
        break;
      }
    }
    
    if (!fieldWrapper) return;

    const errorEl = fieldWrapper.querySelector('.contact__field-error');
    if (errorEl) {
      errorEl.remove();
    }

    // Remove aria attributes
    const input = fieldWrapper.querySelector('select, input[type="checkbox"], input[type="text"], textarea');
    if (input) {
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }
  }

  getErrorIcon() {
    // Return the error icon SVG (matching theme's icon-error.svg)
    return `<svg aria-hidden="true" focusable="false" class="icon icon-error" viewBox="0 0 13 13">
      <circle cx="6.5" cy="6.50049" r="5.5" stroke="white" stroke-width="2"/>
      <circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width="0.7"/>
      <path d="M5.87413 3.52832L5.97439 7.57216H7.02713L7.12739 3.52832H5.87413ZM6.50076 9.66091C6.88091 9.66091 7.18169 9.37267 7.18169 9.00504C7.18169 8.63742 6.88091 8.34917 6.50076 8.34917C6.12061 8.34917 5.81982 8.63742 5.81982 9.00504C5.81982 9.37267 6.12061 9.66091 6.50076 9.66091Z" fill="white"/>
      <path d="M5.87413 3.17832H5.51535L5.52424 3.537L5.6245 7.58083L5.63296 7.92216H5.97439H7.02713H7.36856L7.37702 7.58083L7.47728 3.537L7.48617 3.17832H7.12739H5.87413ZM6.50076 10.0109C7.06121 10.0109 7.5317 9.57872 7.5317 9.00504C7.5317 8.43137 7.06121 7.99918 6.50076 7.99918C5.94031 7.99918 5.46982 8.43137 5.46982 9.00504C5.46982 9.57872 5.94031 10.0109 6.50076 10.0109Z" fill="white" stroke="#EB001B" stroke-width="0.7">
    </svg>`;
  }

  calculateTotalModification() {
    let total = 0;
    Object.values(this.currentModifications).forEach(mod => {
      total += mod.priceModifier;
    });
    return total;
  }

  updateTotalPrice() {
    const totalModification = this.calculateTotalModification();
    const newPrice = this.basePrice + (totalModification * 100); // Convert to cents

    // Update the aggregated total modifier input (for Shopify Function)
    const totalModifierInput = this.container.querySelector('[data-total-price-modifier]');
    if (totalModifierInput) {
      totalModifierInput.value = totalModification;
    }

    // Update price displays
    this.updatePriceDisplay(newPrice);
    
    // Update add to cart button
    this.updateAddToCartButton(newPrice);
  }

  updatePriceDisplay(newPrice) {
    const formattedPrice = this.formatMoney(newPrice);
    
    // Update main price
    if (this.priceElement) {
      const priceDisplay = this.priceElement.querySelector('.price-item--regular, .price__regular .price-item');
      if (priceDisplay) {
        priceDisplay.textContent = formattedPrice;
      }
    }

    // Update mobile price
    if (this.mobilePriceElement) {
      const mobilePriceDisplay = this.mobilePriceElement.querySelector('.price-item--regular, .price__regular .price-item');
      if (mobilePriceDisplay) {
        mobilePriceDisplay.textContent = formattedPrice;
      }
    }
  }

  updateAddToCartButton(newPrice) {
    if (!this.productForm) return;

    const submitButton = this.productForm.querySelector('[type="submit"]');
    if (!submitButton) return;

    const formattedPrice = this.formatMoney(newPrice);
    
    // Update button price if it exists
    const buttonPrice = submitButton.querySelector('.button__price');
    if (buttonPrice) {
      buttonPrice.textContent = formattedPrice;
    }

    // Update data attribute for reference
    submitButton.dataset.price = formattedPrice;
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    
    // Handle different currency formats
    let formatted = this.currencyFormat.replace('{{amount}}', amount);
    formatted = formatted.replace('{{amount_no_decimals}}', Math.round(cents / 100));
    formatted = formatted.replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
    
    return formatted;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPropertyModifiers);
} else {
  initPropertyModifiers();
}

function initPropertyModifiers() {
  const containers = document.querySelectorAll('[data-property-modifiers]');
  containers.forEach(container => {
    new PropertyModifiers(container);
  });
}

// Re-initialize on variant change
document.addEventListener('DOMContentLoaded', () => {
  if (typeof subscribe !== 'undefined') {
    subscribe('variantChange', () => {
      // Wait a moment for DOM to update
      setTimeout(() => {
        initPropertyModifiers();
      }, 100);
    });
  }
});